import { type DateTimeLike, parseDateTimeSafe } from '@racemap/utilities/functions/datetime';
import { Typography } from 'antd';
import { DateTime } from 'luxon';
import { type CSSProperties, useMemo } from 'react';

interface Props {
  time: DateTimeLike | null | undefined;
  format?:
    | Intl.DateTimeFormatOptions
    | string
    | 'iso'
    | 'shortDate'
    | 'time'
    | 'shortTime'
    | 'longTime';
  zone?: string;
  color?: CSSProperties['color'];
  title?: string;
}

const { Text } = Typography;

export const Timestamp: React.FC<Props> = ({ time, format, zone, color, title }) => {
  const dateTime = parseDateTimeSafe(time, zone);
  const timestamp = useMemo(() => {
    if (dateTime == null) {
      return '';
    }
    if (format === 'iso') {
      return dateTime.toISO();
    }
    if (format === 'shortDate') {
      return dateTime.toLocaleString(DateTime.DATE_SHORT);
    }
    if (format === 'time') {
      return dateTime.toLocaleString(DateTime.TIME_24_WITH_SECONDS);
    }
    if (format === 'dayWithTime') {
      return dateTime.toLocaleString({
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      });
    }
    if (format === 'shortTime') {
      return dateTime.toLocaleString(DateTime.TIME_24_SIMPLE);
    }
    if (format === 'longTime') {
      if (dateTime.millisecond !== 0) {
        return `${dateTime.toLocaleString(DateTime.TIME_24_WITH_SECONDS)}.${dateTime.millisecond}`;
      }
      return dateTime.toLocaleString(DateTime.TIME_24_WITH_SECONDS);
    }

    if (typeof format === 'string') {
      return dateTime.toFormat(format);
    }

    if (format) {
      return dateTime.toLocaleString(format);
    }

    return dateTime.toLocaleString(DateTime.DATETIME_MED);
  }, [dateTime, format]);

  const defaultTitle = dateTime ? dateTime.toISO() : 'Invalid Date';

  return (
    <Text style={{ color }} title={title || defaultTitle || undefined}>
      {timestamp}
    </Text>
  );
};
